import { Material, Mesh, MultiMaterial, SubMesh, VertexData } from '@babylonjs/core';

import { MaterialRegistry } from './MaterialRegistry';

export interface MeshFactorySubMesh {
  materialIndex: number;
  verticesStart: number;
  verticesCount: number;
  indexStart: number;
  indexCount: number;
}

interface Options {
  receiveShadows?: boolean;
  materialRegistry?: MaterialRegistry;
}

export class MeshFactory {
  private vertices = Array<number>();
  private indices = Array<number>();
  private normals = Array<number>();
  private uvs = Array<number>();
  private subMeshTable = new Map<number, Array<MeshFactorySubMesh>>();
  private materialRegistry: MaterialRegistry;

  get subMeshes() {
    return [...this.subMeshTable.values()].flatMap((x) => x);
  }

  constructor(
    readonly name: string,
    readonly options?: Options
  ) {
    this.materialRegistry = options && options.materialRegistry ? options.materialRegistry : new MaterialRegistry();
  }

  addVertices(vertices: number[], indices: number[], normals: number[], uvs: number[], material: Material) {
    const verticesStart = this.vertices.length / 3;

    this.vertices = this.vertices.concat(vertices);
    this.indices = this.indices.concat(indices.map((x) => x + verticesStart));
    this.normals = this.normals.concat(normals);
    this.uvs = this.uvs.concat(uvs);

    const verticesCount = vertices.length / 3;
    const indexStart = verticesStart;
    const indexCount = verticesCount;
    const materialIndex = this.materialRegistry.getIndex(material);

    if (!this.subMeshTable.has(materialIndex)) {
      this.subMeshTable.set(materialIndex, []);
    }

    this.subMeshTable.get(materialIndex)!.push({
      materialIndex,
      verticesStart,
      verticesCount,
      indexStart,
      indexCount,
    });
  }

  optimize() {
    // Must follow this order
    this.reorederVertexBuffer();
    this.mergeSameMaterial();
  }

  reorederVertexBuffer() {
    const newSubMeshTable = new Map<number, MeshFactorySubMesh[]>();
    const newVertices = Array<number>();
    const newNormals = Array<number>();
    const newIndices = Array<number>();
    const newUvs = Array<number>();

    for (const [materialIndex, subMeshList] of this.subMeshTable) {
      const newSubMeshList = Array<MeshFactorySubMesh>();

      subMeshList.forEach((subMesh) => {
        const positions = this.vertices.slice(
          subMesh.verticesStart * 3,
          (subMesh.verticesStart + subMesh.verticesCount) * 3
        );
        const normals = this.normals.slice(
          subMesh.verticesStart * 3,
          (subMesh.verticesStart + subMesh.verticesCount) * 3
        );
        const indices = this.indices.slice(subMesh.verticesStart, subMesh.verticesStart + subMesh.verticesCount);
        const uvs = this.uvs.slice(subMesh.verticesStart * 2, (subMesh.verticesStart + subMesh.verticesCount) * 2);

        newSubMeshList.push({
          materialIndex: materialIndex,
          verticesStart: newVertices.length / 3,
          verticesCount: subMesh.verticesCount,
          indexStart: newIndices.length,
          indexCount: subMesh.indexCount,
        });

        newVertices.push(...positions);
        newNormals.push(...normals);
        newIndices.push(...indices);
        newUvs.push(...uvs);
      });

      newSubMeshTable.set(materialIndex, newSubMeshList);
    }

    this.vertices = newVertices;
    this.normals = newNormals;

    // Fix me
    // this.indices = newIndices;
    this.uvs = newUvs;
    this.subMeshTable = newSubMeshTable;
  }

  private mergeSameMaterial() {
    const newSubMeshTable = new Map<number, MeshFactorySubMesh[]>();
    let verticeOffset = 0;
    let indexOffset = 0;
    for (const [materialIndex, subMeshList] of this.subMeshTable) {
      let verticesCount = 0;
      let indexCount = 0;
      subMeshList.forEach((subMesh) => {
        verticesCount += subMesh.verticesCount;
        indexCount += subMesh.indexCount;
      });

      newSubMeshTable.set(materialIndex, [
        {
          materialIndex,
          verticesStart: verticeOffset,
          verticesCount,
          indexStart: indexOffset,
          indexCount,
        },
      ]);

      verticeOffset += verticesCount;
      indexOffset += indexCount;
    }

    this.subMeshTable = newSubMeshTable;
  }

  create(useSubmesh: boolean = true): Mesh {
    if (useSubmesh) {
      const vertexData = new VertexData();
      vertexData.positions = this.vertices;
      vertexData.indices = this.indices;
      vertexData.normals = this.normals;
      vertexData.uvs = this.uvs;

      const material = new MultiMaterial(this.name);
      material.subMaterials = this.materialRegistry ? this.materialRegistry.materials : Array<Material>();

      const mesh = new Mesh(this.name);
      vertexData.applyToMesh(mesh);
      mesh.material = material;
      mesh.receiveShadows = this.options?.receiveShadows ?? false;
      mesh.subMeshes = [];

      for (const x of this.subMeshes) {
        mesh.subMeshes.push(new SubMesh(x.materialIndex, x.verticesStart, x.verticesCount, x.indexStart, x.indexCount, mesh));
      }

      return mesh;
    } else {
      const mesh = new Mesh(this.name);
      mesh.subMeshes = [];

      this.subMeshes.forEach((x, i) => {
        const subVertexData = new VertexData();
        if (this.vertices.length === 0) {
          return;
        }
        subVertexData.positions = this.vertices.slice(x.verticesStart * 3, (x.verticesStart + x.verticesCount) * 3);
        subVertexData.indices = this.indices
          .slice(x.indexStart, x.indexStart + x.indexCount)
          .map((index) => index - x.indexStart);
        subVertexData.normals = this.normals.slice(x.verticesStart * 3, (x.verticesStart + x.verticesCount) * 3);
        subVertexData.uvs = this.uvs.slice(x.verticesStart * 2, (x.verticesStart + x.verticesCount) * 2);

        const sub = new Mesh(`Sub:${i}`);
        subVertexData.applyToMesh(sub);
        sub.material = this.materialRegistry.materials[x.materialIndex] ?? null;
        sub.receiveShadows = this.options?.receiveShadows ?? false;
        sub.parent = mesh;
      });

      return mesh;
    }
  }
}
